import { map } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import AcreageFilter from '@app/models/acreage-filter';
import ListingQueryPastHour from '@app/models/listing-query-past-hour';
import ListingSearchMapPositionList from '@app/models/listing-search-map-position-list';
import ListingSearchResultV2 from '@app/models/listing-search-result-v2';
import { Observable, EMPTY } from 'rxjs';

import { Activity, ListingQuery, ListingSearchMapResult, PagedResults, Position } from '../models';
import { ArrayService } from './array.service';
import { ClientStorageService } from './client-storage.service';
import { ListingService, HttpHelperService, LogService, NotificationService } from '@oa/shared-core';
import { HttpClient, HttpParams } from '@angular/common/http';
import CountyStateFIPS from '@app/models/county-state-fips';

export interface QueryFilter {
  minAcres: number;
  maxAcres: number;
  geohash?: string;
  pageNumber?: number;
  pageSize?: number;
  sortCol?: string;
  sortDir?: string;
  latitude?: number;
  longitude?: number;
  distance?: number;
  fromDate?: Date;
  toDate?: Date;
  showFavorites?: boolean;
  featured?: boolean;
  locationName?: string;
  adults?: number;
  children?: number;
  activities?: string; // comma seperated list of activitiy Ids
  text?: string;
  searchText?: string;
  showPermits?: boolean;
  showExtendedRentals?: boolean;
  showSeasonalRentals?: boolean;
  zipCode?: string;
  showLongTermRentals?: boolean;
  showShortTermRentals?: boolean;
  availableToday: boolean;
  timeZoneOffset: number;
  exorg: string;
  bypassCache: boolean;
}

@Injectable()
export class ListingSearchService {
  private readonly _serviceName = 'ListingSearchService';

  private _query = {} as QueryFilter;
  private _lastQueryStringified = '';
  private searchKey: string;
  private searchResults: PagedResults;
  private getAllForMapResults: ListingSearchMapResult[];
  private _isFirstSearch: boolean;
  private activities: Activity[];

  private _mapCenterOverride: Position;
  maximumPageSize = 10000;

  constructor(
    private http: HttpClient,
    readonly clientStorageService: ClientStorageService,
    readonly arrayService: ArrayService,
    private listingService: ListingService,
    private httpHelper: HttpHelperService,
    private notify: NotificationService,
    private log: LogService
  ) {
    this._isFirstSearch = true;
  }

  /**
   * Is verbose logging enabled
   */
  private get verboseLogging(): boolean {
    return this.log.verboseLogging;
  }

  isFirstSearch(): boolean {
    if (this._isFirstSearch === true) {
      return true;
    }
    return false;
  }

  getActivities(): Observable<Activity[]> {
    if (this.activities !== undefined) {
      return Observable.create((observer) => {
        observer.next(this.activities);
        observer.complete();
      });
    }
    return this.http.get<Activity[]>('/activity').pipe(
      map((data) => {
        this.activities = data;
        return data;
      })
    );
  }

  public cleanFilters(query: QueryFilter) {
    // delete all null/undefined properties
    if (query) {
      for (const prop in query) {
        if (query.hasOwnProperty(prop) && (query[prop] === null || query[prop] === undefined || query[prop] === '')) {
          delete query[prop];
        }
      }
    }

    return query;
  }

  private convertQueryFilterToListingQuery(query: QueryFilter) {
    if (query === undefined) {
      return;
    }

    let filters = {
      bypassCache: query.bypassCache,
      pageNumber: query.pageNumber,
      pageSize: query.pageSize,
      sortCol: query.sortCol,
      sortDir: query.sortDir,
      latitude: query.latitude,
      longitude: query.longitude,
      locationName: query.locationName,
      distance: query.distance,
      fromDate: query.fromDate,
      toDate: query.toDate,
      showFavorites: query.showFavorites,
      featured: query.featured,
      numberOfGuests: query.adults,
      // activities must be sent to the API as a string, not an object so serialize it
      activities: undefined,

      minAcres: query.minAcres,
      maxAcres: query.maxAcres,

      searchText: query.searchText,
      showPermits: query.showPermits,
      showExtendedRentals: query.showExtendedRentals,
      showSeasonalRentals: query.showSeasonalRentals,

      showLongTermRentals: query.showLongTermRentals,
      showShortTermRentals: query.showShortTermRentals,

      zipCode: query.zipCode,
      availableToday: query.availableToday,
      timeZoneOffset: query.timeZoneOffset,
    };

    if (query.activities !== undefined && query.activities.length > 0) {
      try {
        filters.activities = JSON.stringify(
          query.activities.split(',').map((id) => {
            return { id };
          })
        );
      } catch (e) {
        console.error(e);
      }
    }

    // delete all null/undefined properties
    for (const prop in filters) {
      if (filters.hasOwnProperty(prop) && (filters[prop] === null || filters[prop] === undefined)) {
        delete filters[prop];
      }
    }
    return filters;
  }

  /**
   *  Searches listings using the provided query
   */
  searchV2(query?: QueryFilter): Observable<PagedResults> {
    if (this.verboseLogging) {
      console.log(`ListingSearchService.searchV2 query: ${JSON.stringify(query)}`);
    }

    return this.searchListings(query, false);

    
  }

  /**
   * Searches listings using the provided query. useFeaturedListings parameters determines
   * which controller is used listing vs featuredlisting.
   *
   * featuredlisting=true is used by the homepage component to provide featured listings.
   * everyone else sets useFeaturedListings to false
   * @param query
   * @param useFeaturedListings
   */
  private searchListings(query: QueryFilter, useFeaturedListings: boolean): Observable<PagedResults> {
    if (this.verboseLogging) {
      console.log(`ListingSearchService.searchListings useFeaturedListings=${useFeaturedListings}, query: ${JSON.stringify(query)}`);
    }

    // update local storage with the search parameters

    let apiResource = 'listing';

    if (useFeaturedListings) {
      apiResource = 'featuredlisting';
    }

    // The last executed query results are cached so see if we can use them or not
    const key = query ? JSON.stringify(query) : '';

    if (this.verboseLogging) {
      console.log(key);
      console.log(this.searchKey === key);
    }

    if (this.searchKey !== undefined && this.searchResults !== undefined) {
      if (this.searchKey === key) {
        return Observable.create((observer) => {
          observer.next(this.searchResults);
          observer.complete();
        });
      }
    }

    let temp = this.convertQueryFilterToListingQuery(query);

    let params = this.httpHelper.convertToHttpParams(temp);

    // HACK: hardcode to 1 as the API default will hide these and we always want to show in the web
    params = params.append('includeWaitlist', '1');

    this.searchKey = key;

    if (this.verboseLogging) {
      console.log('search params...');
      console.log(params);
    }

    return this.http.get<PagedResults>(`v5/${apiResource}/search`, { params: params });
  }

  /**
   * Executes a search with a Query Filter and returns a list of map positions.  This search does NOT use pages.
   * @param query
   */
  public searchMapPositions(query: QueryFilter): Observable<ListingSearchMapPositionList> {
    if (this.verboseLogging) {
      console.log(`ListingSearchService.searchMapPositions query: ${JSON.stringify(query)}`);
    }
    // update local storage with the search parameters

    let apiResource = 'listing';

    let temp = this.convertQueryFilterToListingQuery(query);

    let params = this.httpHelper.convertToHttpParams(temp);

    if (this.verboseLogging) {
    }
    console.log('ListingSearchService.searchMapPositions params...', params);
    return this.http.get<ListingSearchMapPositionList>(`v4/${apiResource}/search/map`, { params: params });
  }

  /**
   * Searches listings using a query filter
   * @param query
   */
  public search(query?: QueryFilter): Observable<PagedResults> {
    if (this.verboseLogging) {
      console.log(`ListingSearchService.search query: ${JSON.stringify(query)}`);
    }

    // update local storage with the search parameters

    return this.searchListings(query, false);
  }

  public clusterSearch(listingIds: number[]): Observable<PagedResults> {
    
    return this.http.post<PagedResults>(`v5/listing/clusterSearch`, listingIds );
  }

  /**
   * Gets features listings for the Home component.
   * @param query
   */
  getFeaturedListings(query: QueryFilter): Observable<PagedResults> {
    return this.searchListings(query, true);
  }

  /**
   * Executes a free text search for listings
   * @param query
   */
  searchFreeText(query?: ListingQuery): Observable<PagedResults> {
    if (this.verboseLogging) {
      console.log(`ListingSearchService.search query: ${JSON.stringify(query)}`);
    }

    // update local storage with the search parameters

    let params = this.httpHelper.convertToHttpParams(query);

    if (this.verboseLogging) {
      console.log('search params...');
      console.log(params);
    }

    return this.http.get<PagedResults>('listing/text/search', { params: params }).pipe(map((data) => (this.searchResults = data)));
  }

  getNearby(listingId: number): Observable<PagedResults> {
    if (listingId === undefined) {
      
    }

    return this.http.get<PagedResults>(`listing/${listingId}/nearby`);
  }

  /**
   *  Searches listings using the provided query
   */
  getAllForMap(query?: ListingQuery): Observable<ListingSearchMapResult[]> {
    if (this.getAllForMapResults !== undefined) {
      return Observable.create((observer) => {
        observer.next(this.getAllForMapResults);
        observer.complete();
      });
    }

    return this.http.get<ListingSearchMapResult[]>('listing/map').pipe(map((data) => (this.getAllForMapResults = data)));
  }

  /**
   * Gets the listing search query request summary for the past hour
   */
  public getListingQueryPastHour() {
    return this.http.get<ListingQueryPastHour[]>('admin/ListingQuery');
  }

  /**
   * Gets acreage filter items for search filter drop-downs
   */
  public getAcreageFilter() {
    return this.http.get<AcreageFilter[]>('acre');
  }

  /**
   * Gets a sorted list of acreage filters for the max acreage control
   * @param acreageFilters
   */
  public getMaximumAcreageOptions(acreageFilters: AcreageFilter[]): AcreageFilter[] {
    let unsorted = acreageFilters.filter((item) => item.acreage >= 0);

    let sorted = this.arrayService.orderBy(unsorted, 'sortOrder') as AcreageFilter[];

    return sorted;
  }

  /**
   * Gets a sorted list of acreage filters for the minimum acreage control
   * @param acreageFilters
   */
  public getMinimumAcreageOptions(acreageFilters: AcreageFilter[]): AcreageFilter[] {
    let sorted = this.arrayService.orderBy(acreageFilters, 'sortOrder') as AcreageFilter[];

    return sorted;
  }

  public get mapCenterOverride(): Position {
    return this._mapCenterOverride;
  }

  public set mapCenterOverride(v: Position) {
    if (this.verboseLogging) {
      console.log(`${this._serviceName}.mapCenterOverride set v=`, v);
    }

    this._mapCenterOverride = v;
  }

  /**
   * Gets a listing search result for a listing ID
   * @param listingId
   */
  public getListing(listingId: number): Observable<ListingSearchResultV2> {
    return this.listingService.get(listingId).pipe(
      map((listing) => {
        let x = {} as ListingSearchResultV2;
        if (this.verboseLogging) {
          console.log(`${this._serviceName}.getListing(${listingId})`, listing, x);
        }

        return Object.assign(x, listing);
      })
    );
  }

  /**
   * Searches CountyState FIPS by partial county name.  returns an array of CountyStateFIPS (which contain lat/lon).
   * @param county
   */
  public searchCountyState(county: string) {
    if (this.verboseLogging) {
      console.log(`${this._serviceName}.searchCountyState county=`, county);
    }

    if (!county) {
      return EMPTY;
    }

    // 

    const url = `locality/fips/county/search/${county}`;
    return this.http.get<CountyStateFIPS[]>(url);
  }
}
